import { useState } from 'react';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Link,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  meetingsApi,
  type MeetingHome,
  type MeetingRecording,
  type RecordingKind,
} from '@/api/meetings';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { GuardedDialog } from '@/components/GuardedDialog';
import { HeadedCard } from '@/components/HeadedCard';
import { SubmitButton } from '@/components/SubmitButton';
import { useConfirm } from '@/components/useConfirm';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { useDirty } from '@/shared/useDirty';
import { excerpt } from '@/utils/excerpt';

interface SessionRecordingsProps {
  virtualAccountId: MeetingHome;
  meetingId: number;
  occurrenceKey: string;
  recordings: MeetingRecording[];
  canWrite: boolean;
  onChanged: () => void;
}

/** The session's recordings: summary input only, never played here (TMMAPP-0103: its own card). */
export function SessionRecordings({
  virtualAccountId,
  meetingId,
  occurrenceKey,
  recordings,
  canWrite,
  onChanged,
}: SessionRecordingsProps) {
  const { t } = useTranslation();
  const [recordingOpen, setRecordingOpen] = useState(false);
  const [kind, setKind] = useState<RecordingKind>('URL');
  const [url, setUrl] = useState('');
  const [transcript, setTranscript] = useState('');
  const deleting = useConfirm<MeetingRecording>();
  // The recording form empties on every close, so the next opening starts clean.
  const { dirty: recordingDirty } = useDirty({ kind, url, transcript });
  const [addingRecording, setAddingRecording] = useState(false);
  const [recordingError, setRecordingError] = useState(false);
  const closeRecording = () => {
    setRecordingOpen(false);
    setRecordingError(false);
    setKind('URL');
    setUrl('');
    setTranscript('');
  };
  // A save in flight is not dismissible: Esc does not show the discard question.
  const guard = useDialogDirtyGuard(
    recordingOpen && recordingDirty,
    closeRecording,
    addingRecording,
    0,
    recordingOpen,
  );

  async function handleAddRecording() {
    if (addingRecording) return;
    setAddingRecording(true);
    setRecordingError(false);
    try {
      await meetingsApi.addRecording(virtualAccountId, meetingId, occurrenceKey, {
        kind,
        ...(kind === 'URL' ? { url } : { transcript: transcript.slice(0, 262144) }),
      });
      closeRecording();
      onChanged();
    } catch {
      setRecordingError(true);
      guard.focusErrors();
    } finally {
      setAddingRecording(false);
    }
  }

  return (
    <HeadedCard
      title={t('workspace.recordings')}
      subtitle={t('meetings.recordingsHelp')}
      id="session-recordings"
      actions={
        canWrite ? (
          <Button size="small" onClick={() => setRecordingOpen(true)}>
            {t('meetings.addRecording')}
          </Button>
        ) : undefined
      }
    >
      <Stack spacing={1}>
        {recordings.map((row) => (
          <Stack key={row.id} direction="row" sx={{ alignItems: 'center', gap: 1 }}>
            {row.kind === 'URL' && row.url ? (
              <Link href={row.url} target="_blank" rel="noreferrer" sx={{ flex: 1 }}>
                {row.url}
              </Link>
            ) : (
              <Typography variant="body2" sx={{ flex: 1 }}>
                {(row.transcript ?? '').slice(0, 80)}
              </Typography>
            )}
            {canWrite && (
              <Button size="small" color="error" onClick={() => deleting.ask(row)}>
                {t('meetings.deleteRecording')}
              </Button>
            )}
          </Stack>
        ))}
        {recordings.length === 0 && (
          <Typography variant="body2">{t('meetings.session.recordingsEmpty')}</Typography>
        )}
      </Stack>
      {deleting.target !== null && (
        <ConfirmDialog
          title={t('meetings.deleteRecordingTitle', {
            excerpt:
              excerpt(deleting.target.url ?? deleting.target.transcript ?? '') ||
              (deleting.target.kind === 'URL'
                ? t('meetings.recordingUrl')
                : t('meetings.recordingTranscript')),
          })}
          consequence={t('meetings.deleteRecordingConsequence')}
          confirmLabel={t('meetings.deleteRecording')}
          destructive
          busy={deleting.busy}
          error={deleting.error}
          onConfirm={() =>
            void deleting.run(
              async (row) => {
                await meetingsApi.deleteRecording(
                  virtualAccountId,
                  meetingId,
                  occurrenceKey,
                  row.id,
                );
                onChanged();
              },
              () => t('meetings.errorGeneric'),
            )
          }
          onCancel={deleting.cancel}
        />
      )}
      {recordingOpen && (
        <GuardedDialog guard={guard} titleId="rec-title" fullWidth maxWidth="sm">
          <DialogTitle id="rec-title">{t('meetings.addRecording')}</DialogTitle>
          <DialogContent>
            {recordingError && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {t('meetings.recordingError')}
              </Alert>
            )}
            <FormControl>
              <RadioGroup
                value={kind}
                onChange={(event) => setKind(event.target.value as RecordingKind)}
              >
                <FormControlLabel
                  value="URL"
                  control={<Radio />}
                  label={t('meetings.recordingUrl')}
                />
                <FormControlLabel
                  value="TRANSCRIPT_TEXT"
                  control={<Radio />}
                  label={t('meetings.recordingTranscript')}
                />
              </RadioGroup>
            </FormControl>
            {kind === 'URL' ? (
              <TextField
                label={t('meetings.recordingUrl')}
                value={url}
                onChange={(event) => setUrl(event.target.value)}
                fullWidth
                sx={{ mt: 1 }}
              />
            ) : (
              <TextField
                label={t('meetings.recordingTranscript')}
                value={transcript}
                onChange={(event) => setTranscript(event.target.value.slice(0, 262144))}
                multiline
                minRows={6}
                fullWidth
                sx={{ mt: 1 }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={guard.requestClose} disabled={addingRecording}>
              {t('common.cancel')}
            </Button>
            <SubmitButton loading={addingRecording} onClick={() => void handleAddRecording()}>
              {t('common.save')}
            </SubmitButton>
          </DialogActions>
        </GuardedDialog>
      )}
    </HeadedCard>
  );
}
